import React from 'react';
import { Box, IconButton, InputAdornment } from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import CheckCircleOutline from '@mui/icons-material/CheckCircleOutline';
import RadioButtonUnchecked from '@mui/icons-material/RadioButtonUnchecked';
import { PASSWORD_RULES } from './passwordPolicy';

/**
 * Show/hide ("eye") button for a password TextField: pass as `InputProps.endAdornment` and set
 * `type={visible ? 'text' : 'password'}` on the field.
 */
export const PasswordVisibilityToggle = ({
  visible,
  onToggle,
  label,
  disabled,
}: {
  visible: boolean;
  onToggle: () => void;
  /** Field label, used for the button's accessible name ("Show new password"). */
  label: string;
  /** Pass the field's own disabled state so the toggle can't be used on a disabled field. */
  disabled?: boolean;
}) => (
  <InputAdornment position="end">
    <IconButton
      aria-label={`${visible ? 'Hide' : 'Show'} ${label.toLowerCase()}`}
      onClick={onToggle}
      disabled={disabled}
      // Keep focus (and the caret) in the field when toggling.
      onMouseDown={(e) => e.preventDefault()}
      edge="end"
      size="small"
      sx={{ color: '#a1a1aa' }}
    >
      {visible ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
    </IconButton>
  </InputAdornment>
);

/**
 * Live checklist of the password rules (passwordPolicy.ts mirrors the API), so users see what's
 * required before submitting and each rule turns green as it's met.
 */
export const PasswordRequirements = ({ password }: { password: string }) => (
  <Box component="ul" aria-label="Password requirements" aria-live="polite" sx={{ listStyle: 'none', p: 0, m: '4px 0 8px' }}>
    {PASSWORD_RULES.map((rule) => {
      const ok = password.length > 0 && rule.test(password);
      return (
        <Box
          component="li"
          key={rule.id}
          data-met={ok}
          sx={{ display: 'flex', alignItems: 'center', gap: 0.75, fontSize: '0.8rem', color: ok ? '#4ade80' : '#a1a1aa' }}
        >
          {ok ? (
            <CheckCircleOutline sx={{ fontSize: 16 }} role="img" aria-label="Met:" />
          ) : (
            <RadioButtonUnchecked sx={{ fontSize: 16 }} role="img" aria-label="Not met:" />
          )}
          {rule.label}
        </Box>
      );
    })}
  </Box>
);
